import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ComposedChart,
  Area,
  Line,
  CartesianGrid,
  Legend,
  Brush,
} from "recharts";

/**
 * CompanyLogs – single‑file, hook‑safe activity dashboard
 * ------------------------------------------------------
 * • Light/Dark‑aware (tailwind classes via `theme` prop)
 * • Per‑user isolation with instant filtering & date slicing
 * • Timeline view is now the DEFAULT; when ONE date is chosen it flips to
 *   a 24‑hour view so you see exactly *when* the activity happened.
 * • Deep‑link helper that automatically prefixes customer sub‑domain.
 *
 * Props
 *   logs   – Array<{ RecID, LogDateTime, user, Subdomain, Page?, Duration?, LogNote?, LogURL? }>
 *   theme  – "light" | "dark"
 */
function CompanyLogs({ logs = [], theme = "light" }) {
  /* ───────────────────────── helpers (NO hooks inside) */
  const isDark = theme === "dark";
  const cls = (light, dark) => (isDark ? dark : light);
  const brand = isDark ? "#60a5fa" /* blue‑400 */ : "#3b82f6"; /* blue‑500 */
  const avgColor = isDark
    ? "#facc15" /* yellow‑400 */
    : "#d97706"; /* amber‑600 */

  const hms = (sec) => {
    const h = String(Math.floor(sec / 3600)).padStart(2, "0");
    const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
    const s = String(Math.floor(sec % 60)).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  /* deep‑link root (e.g. "ogendeav_schlachter" → "schlachter") */
  const slug = (() => {
    const raw = logs[0]?.Subdomain ?? "";
    return raw.includes("_") ? raw.split("_")[1] : raw;
  })();

  /* ───────────────────────── aggregate expensive work once */
  const prepared = useMemo(() => {
    const byUser = new Map();
    const totals = new Map();

    logs.forEach((l) => {
      const u = l.user ?? "(unknown)";
      if (!byUser.has(u)) {
        byUser.set(u, []);
        totals.set(u, { time: 0, sessions: 0 });
      }
      byUser.get(u).push(l);
      const dur = Number(l.Duration ?? 0);
      const t = totals.get(u);
      t.time += dur;
      t.sessions += 1;
    });

    [...byUser.values()].forEach((arr) =>
      arr.sort((a, b) => new Date(b.LogDateTime) - new Date(a.LogDateTime))
    );

    return {
      grouped: Array.from(byUser.entries()).sort((a, b) =>
        a[0].localeCompare(b[0])
      ),
      totals,
    };
  }, [logs]);

  /* ───────────────────────── UI state */
  const firstUser = prepared.grouped[0]?.[0] ?? "";
  const [activeUser, setActiveUser] = useState(firstUser);
  const [filter, setFilter] = useState("");
  const [view, setView] = useState("timeline"); // "summary" | "timeline" (TIMELINE DEFAULT)
  const [selectedDate, setSelectedDate] = useState(""); // YYYY‑MM‑DD or ""

  /* ───────────────────────── derive filtered entries */
  const entries = useMemo(() => {
    let arr = prepared.grouped.find(([u]) => u === activeUser)?.[1] ?? [];
    if (filter) {
      const q = filter.toLowerCase();
      arr = arr.filter(
        (e) =>
          (e.LogNote ?? "").toLowerCase().includes(q) ||
          (e.Page ?? "").toLowerCase().includes(q)
      );
    }
    if (selectedDate)
      arr = arr.filter((e) => e.LogDateTime.startsWith(selectedDate));
    return arr;
  }, [activeUser, filter, selectedDate, prepared.grouped]);

  /* ───────────────────────── per‑page chart */
  const perPage = useMemo(() => {
    const m = new Map();
    entries.forEach((e) => {
      if (!e.Page || !e.Duration) return;
      m.set(e.Page, (m.get(e.Page) ?? 0) + Number(e.Duration));
    });
    return Array.from(m.entries()).map(([name, sec]) => ({
      name,
      minutes: +(sec / 60).toFixed(1),
    }));
  }, [entries]);

  const buildUrl = (path = "/") => {
    if (!path) return "#";
    if (/^https?:\/\//i.test(path)) return path; // already absolute
    const fixed = path.startsWith("/") ? path : `/${path}`;
    return slug ? `https://${slug}.ogpumper.net${fixed}` : fixed;
  };

  /* ───────────────────────── timeline (all dates) */
  const timelineAll = useMemo(() => {
    const m = new Map(); // date → { minutes, sessions, pages:Set<string> }
    prepared.grouped
      .find(([u]) => u === activeUser)?.[1]
      ?.forEach((e) => {
        const dateKey = e.LogDateTime.slice(0, 10);
        const dur = Number(e.Duration ?? 0);
        if (!m.has(dateKey))
          m.set(dateKey, { minutes: 0, sessions: 0, pages: new Set() });
        const t = m.get(dateKey);
        t.minutes += dur / 60;
        t.sessions += 1;
        if (e.Page) t.pages.add(e.Page);
      });

    return Array.from(m.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, v]) => ({
        date,
        minutes: +v.minutes.toFixed(1),
        sessions: v.sessions,
        avg: v.sessions ? +(v.minutes / v.sessions).toFixed(1) : 0,
        pages: v.pages.size,
      }));
  }, [prepared.grouped, activeUser]);

  /* ───────────────────────── hourly timeline (single‑day) */
  const hourlyTimeline = useMemo(() => {
    if (!selectedDate) return [];
    const buckets = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      label: String(h).padStart(2, "0"),
      minutes: 0,
      sessions: 0,
      pages: 0,
      avg: 0,
    }));

    entries.forEach((e) => {
      const hour = new Date(e.LogDateTime).getHours();
      const durMin = Number(e.Duration ?? 0) / 60;
      const bucket = buckets[hour];
      bucket.minutes += durMin;
      bucket.sessions += 1;
      bucket.pages += e.Page ? 1 : 0;
    });

    buckets.forEach((b) => {
      b.minutes = +b.minutes.toFixed(1);
      b.avg = b.sessions ? +(b.minutes / b.sessions).toFixed(1) : 0;
    });

    return buckets;
  }, [selectedDate, entries]);

  const isHourly = Boolean(selectedDate);
  const timelineData = isHourly ? hourlyTimeline : timelineAll;

  const datePills = timelineAll.map((d) => d.date).reverse();

  /* ───────────────────────── custom tooltip */
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div
        className={cls(
          "rounded border border-gray-200 bg-white p-2 text-xs shadow-sm",
          "rounded border border-gray-600 bg-gray-800 p-2 text-xs shadow-sm"
        )}
      >
        <p className="mb-1 font-semibold">{isHourly ? `${label}:00` : label}</p>
        <p>Minutes: {d.minutes}</p>
        <p>Sessions: {d.sessions}</p>
        <p>Avg / session: {d.avg} min</p>
        {isHourly ? null : <p>Pages visited: {d.pages}</p>}
      </div>
    );
  };

  /* ───────────────────────── guard – empty dataset */
  if (!logs.length)
    return (
      <p
        className={cls(
          "rounded bg-yellow-50 p-3 text-sm text-yellow-800",
          "rounded bg-yellow-900/40 p-3 text-sm text-yellow-200"
        )}
      >
        No log entries yet.
      </p>
    );

  /* ───────────────────────── JSX */
  return (
    <div className="flex w-full flex-col gap-4">
      {/* User selector */}
      <div
        className={cls(
          "sticky top-0 z-10 flex flex-wrap gap-1 rounded bg-gray-50/80 p-2 backdrop-blur",
          "sticky top-0 z-10 flex flex-wrap gap-1 rounded bg-gray-800/80 p-2 backdrop-blur"
        )}
      >
        {prepared.grouped.map(([u]) => (
          <button
            key={u}
            onClick={() => {
              setActiveUser(u);
              setFilter("");
              setSelectedDate("");
            }}
            className={`rounded px-3 py-1 text-sm font-semibold transition-colors ${
              activeUser === u
                ? cls("bg-blue-500 text-white", "bg-blue-600 text-white")
                : cls(
                    "bg-white text-gray-800 hover:bg-blue-500 hover:text-white",
                    "bg-gray-700 text-gray-100 hover:bg-blue-600"
                  )
            }`}
          >
            {u}
            <span className="ml-1 text-xs opacity-70">
              ({prepared.totals.get(u)?.sessions})
            </span>
          </button>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p
            className={cls(
              "text-lg font-semibold",
              "text-lg font-semibold text-gray-100"
            )}
          >
            {activeUser || "—"}
            <span
              className={cls(
                "ml-2 text-sm font-normal text-gray-500",
                "ml-2 text-sm font-normal text-gray-400"
              )}
            >
              {prepared.totals.get(activeUser)?.sessions ?? 0} sessions
            </span>
          </p>
          <p className={cls("text-sm text-gray-600", "text-sm text-gray-300")}>
            Total time: {hms(prepared.totals.get(activeUser)?.time || 0)}
          </p>
        </div>

        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter notes / pages…"
          className={cls(
            "mt-2 w-full rounded border border-gray-300 px-3 py-1 text-sm focus:border-blue-400 focus:outline-none sm:mt-0 sm:max-w-xs",
            "mt-2 w-full rounded border-gray-600 bg-gray-700 px-3 py-1 text-sm text-gray-100 focus:border-blue-500 focus:outline-none sm:mt-0 sm:max-w-xs"
          )}
        />
      </div>

      {/* View toggle */}
      <div className="flex gap-2 self-end text-sm">
        {[
          { id: "summary", label: "Per-page" },
          { id: "timeline", label: "Timeline" },
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => {
              setView(id);
              setSelectedDate("");
            }}
            className={`rounded px-3 py-1 font-medium transition-colors ${
              view === id
                ? cls("bg-blue-500 text-white", "bg-blue-600 text-white")
                : cls(
                    "bg-gray-200 text-gray-700 hover:bg-gray-300",
                    "bg-gray-700 text-gray-200 hover:bg-gray-600"
                  )
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Date pills (only in timeline view) */}
      {view === "timeline" && (
        <div className="flex flex-wrap gap-1 self-end text-xs">
          <button
            onClick={() => setSelectedDate("")}
            className={`rounded px-2 py-1 transition-colors ${
              !selectedDate
                ? cls("bg-blue-500 text-white", "bg-blue-600 text-white")
                : cls(
                    "bg-gray-200 text-gray-700 hover:bg-gray-300",
                    "bg-gray-700 text-gray-200 hover:bg-gray-600"
                  )
            }`}
          >
            All dates
          </button>
          {datePills.map((d) => (
            <button
              key={d}
              onClick={() => setSelectedDate(selectedDate === d ? "" : d)}
              className={`rounded px-2 py-1 transition-colors ${
                selectedDate === d
                  ? cls("bg-blue-500 text-white", "bg-blue-600 text-white")
                  : cls(
                      "bg-gray-200 text-gray-700 hover:bg-gray-300",
                      "bg-gray-700 text-gray-200 hover:bg-gray-600"
                    )
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      )}

      {/* Charts */}
      {view === "summary" && perPage.length > 0 && (
        <div
          className={cls(
            "h-56 w-full overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow",
            "h-56 w-full overflow-hidden rounded-lg border border-gray-600 bg-gray-800 p-4 shadow"
          )}
        >
          <p
            className={cls(
              "mb-2 text-xs font-medium text-gray-500",
              "mb-2 text-xs font-medium text-gray-400"
            )}
          >
            Minutes per page
          </p>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={perPage} layout="vertical" margin={{ left: 60 }}>
              <XAxis
                type="number"
                domain={[0, (max) => Math.ceil(max * 1.15)]}
                tickFormatter={(v) => `${v}m`}
                tick={{ fontSize: 10, fill: cls("#374151", "#d1d5db") }}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={160}
                tick={{ fontSize: 11, fill: cls("#374151", "#d1d5db") }}
              />
              <Tooltip
                formatter={(v) => `${v} min`}
                contentStyle={{
                  background: isDark ? "#1f2937" : "#ffffff",
                  borderColor: isDark ? "#4b5563" : "#e5e7eb",
                }}
              />
              <Bar dataKey="minutes" fill={brand} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === "timeline" && timelineData.length > 0 && (
        <div
          className={cls(
            "h-64 w-full overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow",
            "h-64 w-full overflow-hidden rounded-lg border border-gray-600 bg-gray-800 p-4 shadow"
          )}
        >
          <p
            className={cls(
              "mb-2 text-xs font-medium text-gray-500",
              "mb-2 text-xs font-medium text-gray-400"
            )}
          >
            {isHourly
              ? `Hourly activity for ${selectedDate}`
              : "Daily activity (minutes ▇ & sessions ⎯)"}
          </p>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={timelineData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient
                  id="minutesGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor={brand} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={brand} stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />

              {/* X‑axis adapts to hourly or daily mode */}
              {isHourly ? (
                <XAxis
                  dataKey="label"
                  height={30}
                  tick={{ fontSize: 10, fill: cls("#374151", "#d1d5db") }}
                  interval={0}
                />
              ) : (
                <XAxis
                  dataKey="date"
                  angle={-45}
                  height={40}
                  interval={Math.max(
                    0,
                    Math.floor(timelineData.length / 7) - 1
                  )}
                  textAnchor="end"
                  tick={{ fontSize: 10, fill: cls("#374151", "#d1d5db") }}
                />
              )}

              {/* Y‑axes (minutes on left, sessions on right) */}
              <YAxis
                yAxisId="left"
                domain={[0, (max) => Math.ceil(max * 1.15)]}
                width={40}
                tick={{ fill: cls("#374151", "#d1d5db") }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, (max) => Math.max(5, Math.ceil(max * 1.15))]}
                width={40}
                tick={{ fill: cls("#374151", "#d1d5db") }}
              />

              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ color: cls("#374151", "#d1d5db") }} />

              <Area
                yAxisId="left"
                type="monotone"
                dataKey="minutes"
                name="Minutes"
                stroke={brand}
                fill="url(#minutesGradient)"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="sessions"
                name="Sessions"
                stroke={cls("#9ca3af", "#e5e7eb")}
                strokeWidth={2}
                dot={false}
              />
              {!isHourly && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="avg"
                  name="Avg/min"
                  stroke={avgColor}
                  strokeDasharray="4 4"
                  dot={{ r: 2 }}
                />
              )}
              {!isHourly && (
                <Brush
                  dataKey="date"
                  height={10}
                  stroke={brand}
                  travellerWidth={8}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Entries list */}
      <div className="max-h-96 overflow-y-auto pr-1">
        {entries.map((e) => (
          <div
            key={e.RecID}
            className={cls(
              "mb-2 rounded border border-gray-300 bg-white p-3 shadow-sm transition-transform hover:scale-[1.01]",
              "mb-2 rounded border border-gray-600 bg-gray-700 p-3 shadow-sm transition-transform hover:scale-[1.01]"
            )}
          >
            <div className="mb-1 flex flex-wrap items-baseline gap-1 text-sm font-medium">
              <time
                dateTime={e.LogDateTime}
                className={cls("text-gray-600", "text-gray-300")}
              >
                {new Date(e.LogDateTime).toLocaleString()}
              </time>

              {e.Page && (
                <a
                  href={buildUrl(e.Page)}
                  target="_blank"
                  rel="noreferrer"
                  className={cls(
                    "text-xs text-purple-600 underline-offset-2 hover:underline",
                    "text-xs text-purple-400 underline-offset-2 hover:underline"
                  )}
                >
                  {e.Page}
                </a>
              )}

              {e.Duration && (
                <span
                  className={cls(
                    "text-xs text-gray-500",
                    "text-xs text-gray-400"
                  )}
                >
                  {hms(e.Duration)}
                </span>
              )}

              {e.LogURL && (
                <a
                  href={buildUrl(e.LogURL)}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-auto text-xs underline-offset-2 hover:underline"
                >
                  details
                </a>
              )}
            </div>

            <p
              className={cls(
                "text-sm leading-snug text-gray-700",
                "text-sm leading-snug text-gray-200"
              )}
            >
              {e.LogNote || "—"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CompanyLogs;
